/**
 * Frontend FX helper. Storage is EUR-only on the server; this is purely
 * for display-time conversions.
 *
 * Rates returned by GET /api/fx/latest are EUR-base — i.e.
 * `rates["USD"]` is "USD per 1 EUR". So to display an EUR amount as USD:
 *   usd_amount = eur_amount * rates["USD"]
 */

import { useEffect, useState } from 'react';
import { getClient } from '@/lib/api';

export type FxRates = Record<string, number>;

export function useFxRates(): { rates: FxRates | null; error: string | null } {
  const [rates, setRates] = useState<FxRates | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const c = getClient();
    if (!c) return;
    void (async () => {
      const { data, error: err } = await c.GET('/api/fx/latest');
      if (cancelled) return;
      if (err) {
        setError(JSON.stringify(err));
        return;
      }
      const out: FxRates = {};
      for (const [code, rate] of Object.entries(data?.rates ?? {})) {
        const n = Number(rate);
        if (Number.isFinite(n) && n > 0) out[code.toUpperCase()] = n;
      }
      setRates(out);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { rates, error };
}

/** EUR amount → display-currency amount. Falls back to EUR pass-through
 * if the target currency is unknown or rates haven't loaded yet. */
export function fromEur(
  eurAmount: number | string | null | undefined,
  target: string,
  rates: FxRates | null,
): number | null {
  if (eurAmount === null || eurAmount === undefined) return null;
  const n = typeof eurAmount === 'string' ? Number(eurAmount) : eurAmount;
  if (!Number.isFinite(n)) return null;
  const code = target.toUpperCase();
  if (code === 'EUR') return n;
  if (!rates) return n; // not loaded yet — show the EUR number
  const rate = rates[code];
  if (!rate) return n;
  return n * rate;
}
